import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useLocation } from 'react-router-dom';

const Issues = () => {
  const { user } = useAuth();
  const { projectId } = useParams();
  const location = useLocation();
  const isAdminView = location.pathname.startsWith('/admin');

  const [project, setProject] = useState(null);
  const [issues, setIssues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [issueForm, setIssueForm] = useState({
    title: '',
    description: '',
    type: 'bug',
    priority: 'medium',
    status: 'pending',
    developer_notes: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIssuesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectId]);

  const fetchIssuesData = async () => {
    try {
      let projectData;
      if (isAdminView && projectId) {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();
        if (error) throw error;
        projectData = data;
      } else {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('client_id', user.id)
          .single();
        if (error) throw error;
        projectData = data;
      }
      setProject(projectData);

      const { data: issuesData, error: issuesError } = await supabase
        .from('issues')
        .select('*')
        .eq('project_id', projectData.id)
        .order('created_at', { ascending: false });

      if (issuesError) throw issuesError;
      setIssues(issuesData);
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIssue = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await supabase
        .from('issues')
        .insert([{
          project_id: project.id,
          submitted_by: user.id,
          title: issueForm.title,
          description: issueForm.description,
          type: issueForm.type,
          priority: issueForm.priority,
          status: issueForm.status,
          developer_notes: issueForm.developer_notes || null
        }])
        .select()
        .single();

      if (error) throw error;

      setIssues([data, ...issues]);
      setShowModal(false);
      setIssueForm({ title: '', description: '', type: 'bug', priority: 'medium', status: 'pending', developer_notes: '' });
    } catch (error) {
      console.error('Error creating issue:', error);
      alert('Failed to create issue: ' + (error.message || 'Unknown error'));
    }
  };

  const handleUpdateIssue = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('issues')
        .update({
          title: issueForm.title,
          description: issueForm.description,
          type: issueForm.type,
          priority: issueForm.priority,
          status: issueForm.status,
          developer_notes: issueForm.developer_notes
        })
        .eq('id', editingIssue.id);

      if (error) throw error;

      setIssues(issues.map(i => i.id === editingIssue.id ? { ...i, ...issueForm } : i));
      setShowModal(false);
      setEditingIssue(null);
      setIssueForm({ title: '', description: '', type: 'bug', priority: 'medium', status: 'pending', developer_notes: '' });
    } catch (error) {
      console.error('Error updating issue:', error);
      alert('Failed to update issue.');
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Are you sure you want to delete this issue?')) return;

    try {
      const { error } = await supabase
        .from('issues')
        .delete()
        .eq('id', issueId);

      if (error) throw error;

      setIssues(issues.filter(i => i.id !== issueId));
    } catch (error) {
      console.error('Error deleting issue:', error);
      alert('Failed to delete issue.');
    }
  };

  const openEditModal = (issue) => {
    setEditingIssue(issue);
    setIssueForm({
      title: issue.title,
      description: issue.description,
      type: issue.type,
      priority: issue.priority,
      status: issue.status,
      developer_notes: issue.developer_notes || ''
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingIssue(null);
    setIssueForm({ title: '', description: '', type: 'bug', priority: 'medium', status: 'pending', developer_notes: '' });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'text-gray-600',
      'medium': 'text-yellow-600',
      'high': 'text-orange-600',
      'critical': 'text-red-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8 flex items-center justify-between"
      >
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Issues & Support</h1>
          <p className="text-gray-600">Report bugs and track support requests.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
        >
          + Report Issue
        </button>
      </motion.div>

      <div className="space-y-4">
        {issues.map((issue, index) => (
          <motion.div
            key={issue.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.05 }}
            className="bg-white border border-gray-200 rounded-2xl p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(issue.status)}`}>
                    {issue.status.replace('-', ' ').toUpperCase()}
                  </span>
                  <span className={`text-sm font-semibold uppercase ${getPriorityColor(issue.priority)}`}>
                    {issue.priority}
                  </span>
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {issue.type.replace('-', ' ')}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">{issue.title}</h3>
              </div>
              {isAdminView && (
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => openEditModal(issue)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xs font-medium transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteIssue(issue.id)}
                    className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>

            <p className="text-gray-600 mb-4 whitespace-pre-wrap">{issue.description}</p>

            {issue.developer_notes && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-900 mb-1">Developer Notes:</p>
                <p className="text-blue-800 whitespace-pre-wrap">{issue.developer_notes}</p>
              </div>
            )}

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">Reported: {formatDate(issue.created_at)}</p>
              <p className="text-sm text-gray-500">ID: #{issue.id.slice(0, 8)}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {issues.length === 0 && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-gray-600 mb-4">No issues reported yet.</p>
        </div>
      )}

      {/* Issue Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowModal(false);
              setEditingIssue(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {editingIssue ? 'Edit Issue' : 'Report Issue'}
              </h3>
              <form onSubmit={editingIssue ? handleUpdateIssue : handleCreateIssue}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                      <select
                        value={issueForm.type}
                        onChange={(e) => setIssueForm({ ...issueForm, type: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="bug">Bug</option>
                        <option value="feature-request">Feature Request</option>
                        <option value="question">Question</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                      <select
                        value={issueForm.priority}
                        onChange={(e) => setIssueForm({ ...issueForm, priority: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  {isAdminView && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                      <select
                        value={issueForm.status}
                        onChange={(e) => setIssueForm({ ...issueForm, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={issueForm.title}
                      onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Brief description of the issue"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={issueForm.description}
                      onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Provide detailed information about the issue..."
                      rows="4"
                      required
                    />
                  </div>

                  {isAdminView && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Developer Notes</label>
                      <textarea
                        value={issueForm.developer_notes}
                        onChange={(e) => setIssueForm({ ...issueForm, developer_notes: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder="Internal notes for developers..."
                        rows="3"
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingIssue(null);
                    }}
                    className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    {editingIssue ? 'Update Issue' : 'Submit Issue'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Issues;
